import React, { useState } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  HStack,
  Field,
  Alert,
} from '@chakra-ui/react'
import { FcGoogle } from 'react-icons/fc'
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthService } from '../../services/authService'
import { Logo } from '../Brand/Logo'
import { API_BASE_URL } from '../../config/api'
import { TwoFactorVerification } from './TwoFactorVerification'
import { twoFactorAuthService } from '../../services/twoFactorAuthService'
import { PasswordInput } from '../ui/PasswordInput'
import { OAuthLegalNotice } from '../Legal/OAuthLegalNotice'
import {
  consumePendingLoginRedirect,
  persistPendingLoginRedirect,
  resolveLoginReturnTo,
} from '../../utils/authRedirect'

interface LoginFormData {
  email: string
  password: string
}

interface LoginFormErrors {
  email?: string
  password?: string
  general?: string
}

export const LoginForm: React.FC = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState<LoginFormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [show2FA, setShow2FA] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  
  // Get the redirect URL after successful login
  const from = resolveLoginReturnTo(location, searchParams)

  const handleRedirect = () => {
    const token = searchParams.get('token')
    consumePendingLoginRedirect()
    if (token) {
      navigate(`/accept-invite/${token}`, { replace: true })
      return
    }
    navigate(from, { replace: true })
  }

  // Input validation
  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = t('auth.email_required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.email_invalid')
    }

    if (!formData.password.trim()) {
      newErrors.password = t('auth.password_required')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    
    // Clear specific field error when user starts typing
    if (errors[name as keyof LoginFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  // Handle email/password login
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsLoading(true)
    setErrors({})

    try {
      await AuthService.signIn(formData.email, formData.password)
      
      // Check if 2FA is enabled for this user
      try {
        const status = await twoFactorAuthService.getStatus()
        if (status.enabled) {
          // Show 2FA verification modal
          // Note: Device trust checking would require a separate API endpoint
          // For now, we always require 2FA verification if enabled
          const token = searchParams.get('token')
          const target = token ? `/accept-invite/${token}` : from
          setPendingNavigation(target)
          setShow2FA(true)
          setIsLoading(false)
          return
        }
      } catch (error) {
        // If 2FA check fails, continue with normal login
        console.warn('Failed to check 2FA status, continuing with login:', error)
      }
      
      handleRedirect()
    } catch (error) {
      const err: any = error
      const code = err?.originalError?.code || ''

      // Handle both old and new Firebase error codes for V3 migration
      // auth/user-not-found: Old error code
      // auth/invalid-credential: New error code (prevents user enumeration)
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        // Check with backend: is this a V4 user with wrong password, or a V3 migration case?
        try {
          const resp = await fetch(`${API_BASE_URL}/api/v1/auth/auto-provision-v3`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email.trim().toLowerCase() })
          })
          
          // Parse response to determine if this is wrong password or migration case
          const data = await resp.json().catch(() => ({ 
            message: 'If this email is eligible, a password reset link will be sent.',
            is_wrong_password: false
          }))
          
          setErrors({
            general: data.message
          })
          
          // Only clear form if this is a migration case (password reset email sent)
          // For wrong password, keep the email so user can retry with correct password
          if (data.is_wrong_password) {
            // V4 user with wrong password - keep email, clear only password
            setFormData(prev => ({ ...prev, password: '' }))
          } else {
            // V3 migration or non-existent user - clear form for security
            setFormData({ email: '', password: '' })
          }
        } catch (apErr) {
          setErrors({
            general: 'If this email is eligible, a password reset link will be sent.'
          })
          // Clear form fields for better UX
          setFormData({ email: '', password: '' })
        }
      } else if (code === 'auth/wrong-password') {
        // Explicit wrong password (older Firebase behavior) → show generic error message
        setErrors({
          general: 'Invalid email or password. Please try again or use "Forgot Password".'
        })
      } else {
        // Other errors → use existing error handling
        setErrors({
          general: error instanceof Error ? error.message : t('auth.login_failed'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Handle Google login
  const handleGoogleLogin = async () => {
    setIsLoading(true)
    setErrors({})

    try {
      persistPendingLoginRedirect(from)
      await AuthService.signInWithGoogle()
      
      // Skip 2FA check - let the auth flow complete naturally
      // The AuthContext will handle the rest
      handleRedirect()
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : t('auth.google_signin_failed'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Handle 2FA verification completion
  const handle2FAVerify = (success: boolean) => {
    if (success && pendingNavigation) {
      setShow2FA(false)
      navigate(pendingNavigation, { replace: true })
      setPendingNavigation(null)
    } else if (!success) {
      // If verification fails, user stays on login page
      setShow2FA(false)
      setPendingNavigation(null)
    }
  }

  return (
    <Box
      w="full"
      maxW="md"
      mx="auto"
      p={8}
      bg="bg"
      borderRadius="lg"
      shadow="md"
      border="1px"
      borderColor="border"
    >
      <VStack gap={6} align="stretch">
        {/* Header */}
        <VStack gap={4}>
          <Logo size="2xl" />
          <VStack gap={2}>
            <Heading as="h2" size="md" color="green.600" _dark={{ color: 'green.400' }} textAlign="center">
              {t('auth.welcome_back')}
            </Heading>
            <Text color="fg.muted" textAlign="center">
              {t('auth.sign_in_subtitle')}
            </Text>
          </VStack>
        </VStack>

        {/* Error Alert */}
        {errors.general && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            <Alert.Content>
              {errors.general}
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Google Login Button */}
        <Button
          onClick={handleGoogleLogin}
          loading={isLoading}
          variant="outline"
          borderColor="border"
          _hover={{ bg: 'bg.muted' }}
          size="lg"
          disabled={isLoading}
        >
          <HStack gap={2}>
            <FcGoogle />
            <Text>{t('auth.continue_with_google')}</Text>
          </HStack>
        </Button>

        <OAuthLegalNotice />

        <HStack align="center" w="full">
          <Box flex="1" h="1px" bg="border" />
          <Text color="fg.muted" fontSize="sm" px={3}>
            {t('common.or')}
          </Text>
          <Box flex="1" h="1px" bg="border" />
        </HStack>

        {/* Email/Password Form */}
        <Box as="form" onSubmit={handleEmailLogin}>
          <VStack gap={4}>
            <Field.Root invalid={!!errors.email} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                {t('common.email')}
              </Field.Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('auth.enter_email')}
                bg="bg"
                color="fg"
                borderColor={errors.email ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="email"
              />
              <Field.ErrorText>{errors.email}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors.password} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                {t('common.password')}
              </Field.Label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('auth.enter_password')}
                bg="bg"
                color="fg"
                borderColor={errors.password ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="current-password"
              />
              <Field.ErrorText>{errors.password}</Field.ErrorText>
            </Field.Root>

            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              w="full"
              loading={isLoading}
              disabled={isLoading}
            >
              {t('common.sign_in')}
            </Button>
          </VStack>
        </Box>

        {/* Links */}
        <VStack gap={2}>
          <RouterLink to="/forgot-password">
            <Text
              color="green.600"
              _dark={{ color: 'green.400' }}
              fontSize="sm"
              textAlign="center"
              _hover={{ textDecoration: 'underline' }}
            >
              {t('auth.forgot_password')}
            </Text>
          </RouterLink>
          
          <Text fontSize="sm" color="fg.muted" textAlign="center">
            {t('auth.no_account')}{' '}
            <RouterLink to={`/register${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}>
              <Text
                as="span"
                color="green.600"
                _dark={{ color: 'green.400' }}
                fontWeight="medium"
                _hover={{ textDecoration: 'underline' }}
              >
                {t('common.sign_up')}
              </Text>
            </RouterLink>
          </Text>
        </VStack>
      </VStack>

      {/* 2FA Verification Modal */}
      <TwoFactorVerification
        isOpen={show2FA}
        onVerify={handle2FAVerify}
        title="Two-Factor Authentication Required"
        description="Enter the 6-digit code from your authenticator app to complete login"
        allowSkip={false}
      />
    </Box>
  )
}

export default LoginForm 
